const FREQUENT_STORES_KEY = 'kfc.frequentStores';
const RECENT_STORE_KEYWORDS_KEY = 'kfc.storeQuery.recentKeywords.v1';
const SELECTED_STORE_KEY = 'kfc.selectedStore';
const STORE_LIST_STATE_KEY = 'kfc.storeListState';
const STORE_LIST_RESTORE_KEY = 'kfc.shouldRestoreStoreListState';
const STORE_LIST_STATE_MAX_AGE = 10 * 60 * 1000;
const MAX_FREQUENT_STORES = 5;
const MAX_RECENT_STORE_KEYWORDS = 5;
const DIRECT_STORE_CODE_PATTERN = /^[A-Z0-9_-]{3,32}$/;

function readJson (key, fallback) {
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson (key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage may be unavailable in private mode.
  }
}

function normalizeFullwidthText (value) {
  return String(value || '')
    .replace(/[\uFF01-\uFF5E]/g, char => String.fromCharCode(char.charCodeAt(0) - 0xFEE0))
    .replace(/\u3000/g, ' ');
}

export function parseDirectStoreCodeText (value) {
  const rawText = normalizeFullwidthText(value).trim();
  if (!rawText || /[@#\s\u4E00-\u9FFF]/.test(rawText)) {
    return null;
  }

  const storeCode = rawText.toUpperCase();
  if (!DIRECT_STORE_CODE_PATTERN.test(storeCode) || !/[0-9]/.test(storeCode)) {
    return null;
  }

  return {rawText, storeCode};
}

function normalizeRecentKeyword (keyword) {
  return String(keyword || '').trim().replace(/\s+/g, ' ');
}

function isRecentKeywordEligible (keyword) {
  const value = normalizeRecentKeyword(keyword);
  return !!value &&
    !value.startsWith('#') &&
    !value.startsWith('＃') &&
    !parseDirectStoreCodeText(value);
}

function mergeRecentKeywords (keywordLists) {
  const output = [];
  const seen = new Set();

  keywordLists.forEach(keywords => {
    if (!Array.isArray(keywords)) return;
    keywords.forEach(keyword => {
      const value = normalizeRecentKeyword(keyword);
      const normalizedValue = value.toLocaleLowerCase();
      if (!isRecentKeywordEligible(value) || seen.has(normalizedValue)) return;
      seen.add(normalizedValue);
      output.push(value);
    });
  });

  return output.slice(0, MAX_RECENT_STORE_KEYWORDS);
}

export function loadRecentStoreKeywords () {
  return mergeRecentKeywords([readJson(RECENT_STORE_KEYWORDS_KEY, [])]);
}

export function saveRecentStoreKeyword (keyword) {
  const value = normalizeRecentKeyword(keyword);
  if (!isRecentKeywordEligible(value)) {
    return loadRecentStoreKeywords();
  }

  const keywords = mergeRecentKeywords([[value], loadRecentStoreKeywords()]);
  writeJson(RECENT_STORE_KEYWORDS_KEY, keywords);
  return keywords;
}

function readSessionJson (key, fallback) {
  try {
    const value = window.sessionStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function writeSessionJson (key, value) {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // sessionStorage may be unavailable in private mode.
  }
}

function removeSessionJson (key) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // sessionStorage may be unavailable in private mode.
  }
}

export function getStoreKey (store) {
  if (!store) {
    return '';
  }
  return String(store.storecode || `${store.storename || ''}-${store.address || ''}`);
}

export function hasSubStore (store) {
  return !!(
    store &&
    store.subStores &&
    store.subStores.length > 0 &&
    store.subStores[0] &&
    store.subStores[0].storename
  );
}

export function getPickupPlaces (store) {
  if (!store) {
    return [];
  }

  const places = [{
    index: 0,
    name: store.takeMealPlace || '默认取餐口',
    storecode: store.storecode,
    status: store.status,
    disabled: isSpeedPickupPlace(store.takeMealPlace)
  }];

  if (hasSubStore(store)) {
    places.push({
      index: 1,
      name: store.subStores[0].storename,
      storecode: store.subStores[0].storecode,
      status: store.subStores[0].status,
      disabled: isSpeedPickupPlace(store.subStores[0].storename)
    });
  }

  return places;
}

export function isSpeedPickupPlace (name) {
  return String(name || '').indexOf('车速取') !== -1;
}

function parseBusinessTime (value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(value || '').trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return {
    minutes: hour * 60 + minute,
    text: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
  };
}

export function is24HourStore (store) {
  const start = parseBusinessTime(store && store.starttime);
  const end = parseBusinessTime(store && store.endtime);
  if (!start || !end) return false;
  return start.minutes === end.minutes || start.minutes === (end.minutes + 1) % (24 * 60);
}

export function formatBusinessHours (store) {
  if (is24HourStore(store)) return '24小时营业';
  const start = parseBusinessTime(store && store.starttime);
  const end = parseBusinessTime(store && store.endtime);
  if (!start || !end) {
    return [store && store.starttime, store && store.endtime].filter(Boolean).join('-') || '暂无';
  }
  return end.minutes < start.minutes
    ? `${start.text}-次日${end.text}`
    : `${start.text}-${end.text}`;
}

export function getDenseReminder (store) {
  if (!store || Number(store.status) !== 1) return '';
  const dense = store.reminders && store.reminders.dense;
  return typeof dense === 'string' && dense.trim()
    ? '门店密集，请注意区分'
    : '';
}

export function isStoreOpenNow (store, now = new Date()) {
  if (!store || Number(store.status) !== 1) {
    return false;
  }

  if (is24HourStore(store)) {
    return true;
  }

  const start = parseBusinessTime(store.starttime);
  const end = parseBusinessTime(store.endtime);
  if (!start || !end) {
    return true;
  }

  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  if (end.minutes < start.minutes) {
    return currentMinutes >= start.minutes || currentMinutes <= end.minutes;
  }
  return currentMinutes >= start.minutes && currentMinutes <= end.minutes;
}

export function isPickupDisabled (store, pickupIndex) {
  const places = getPickupPlaces(store);
  let place = places[0];

  places.some(item => {
    if (item.index === pickupIndex) {
      place = item;
      return true;
    }
    return false;
  });

  return !place || place.disabled || Number(place.status) !== 1 || !isStoreOpenNow(store);
}

export function getStoreStatusText (store) {
  if (!store || Number(store.status) !== 1) {
    return '暂停营业';
  }

  return isStoreOpenNow(store) ? '营业中' : '休息中';
}

export function getStoreStatusClass (store) {
  return store && Number(store.status) === 1 && isStoreOpenNow(store) ? 'open' : 'closed';
}

export function formatDistance (distance) {
  if (!distance) {
    return '';
  }

  return distance >= 1000 ? (distance / 1000).toFixed(2) + ' 千米' : Math.floor(distance) + ' 米';
}

export function buildStoreInfo (store, pickupIndex, pickupMethod) {
  if (!store) {
    return '';
  }

  let storeInfo = `@${store.cityName || ''}@${store.storename || ''}`;
  const address = store.address || '';

  if (hasSubStore(store)) {
    const useSubStore = pickupIndex === 1;
    const pickupPlace = useSubStore ? store.subStores[0].storename : store.takeMealPlace;
    const storeCode = useSubStore ? store.subStores[0].storecode : store.storecode;

    storeInfo = `${storeCode}${storeInfo}`;
    if (pickupPlace) {
      storeInfo += `#${pickupPlace}`;
    }
    storeInfo += `@${address}`;
  } else {
    storeInfo = `${store.storecode}${storeInfo}@${address}`;
  }

  if (pickupMethod) {
    storeInfo += `@${pickupMethod}`;
  }

  return storeInfo;
}

export function saveSelectedStore (store) {
  writeJson(SELECTED_STORE_KEY, store || {});
  writeSessionJson(SELECTED_STORE_KEY, store || {});
}

export function loadSelectedStore () {
  return readJson(SELECTED_STORE_KEY, null) || readSessionJson(SELECTED_STORE_KEY, {});
}

export function findStoredStoreByKey (storeKey) {
  const selectedStore = loadSelectedStore();
  const stores = [selectedStore].concat(loadFrequentStores());
  const key = String(storeKey || '');

  if (!key) {
    return selectedStore || {};
  }

  return stores.find(store => getStoreKey(store) === key) || {};
}

export function saveStoreListState (state) {
  writeSessionJson(
    STORE_LIST_STATE_KEY,
    Object.assign({}, state, {savedAt: Date.now()})
  );
}

export function loadStoreListState () {
  const state = readSessionJson(STORE_LIST_STATE_KEY, null);

  if (!state || !state.savedAt) {
    return null;
  }

  if (Date.now() - state.savedAt > STORE_LIST_STATE_MAX_AGE) {
    return null;
  }

  return state;
}

export function markStoreListStateForRestore () {
  writeSessionJson(STORE_LIST_RESTORE_KEY, true);
}

export function consumeStoreListStateRestoreFlag () {
  const shouldRestore = readSessionJson(STORE_LIST_RESTORE_KEY, false) === true;

  if (shouldRestore) {
    removeSessionJson(STORE_LIST_RESTORE_KEY);
  }

  return shouldRestore;
}

export function clearStoreListStateRestoreFlag () {
  removeSessionJson(STORE_LIST_RESTORE_KEY);
}

export function loadFrequentStores () {
  return readJson(FREQUENT_STORES_KEY, [])
    .sort((prev, next) => (next.lastVisitedAt || 0) - (prev.lastVisitedAt || 0))
    .slice(0, MAX_FREQUENT_STORES);
}

export function saveFrequentStore (store) {
  if (!store) {
    return [];
  }

  const storeKey = getStoreKey(store);
  const stores = loadFrequentStores().filter(item => getStoreKey(item) !== storeKey);
  const nextStores = [Object.assign({}, store, {lastVisitedAt: Date.now()})]
    .concat(stores)
    .slice(0, MAX_FREQUENT_STORES);

  writeJson(FREQUENT_STORES_KEY, nextStores);
  return nextStores;
}

export function removeFrequentStore (store) {
  const storeKey = getStoreKey(store);
  const nextStores = loadFrequentStores().filter(item => getStoreKey(item) !== storeKey);

  writeJson(FREQUENT_STORES_KEY, nextStores);
  return nextStores;
}

export function copyText (text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text).then(() => true);
  }

  return new Promise(resolve => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    let success = false;
    try {
      success = document.execCommand('copy');
    } catch {
      success = false;
    }

    document.body.removeChild(textarea);
    resolve(success);
  });
}
